import { useMemo } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ShowStatsOutput } from '@schemas'
import { useShowStats } from '@/api/hooks'
import { cn } from '@/lib/utils'

export type ShowStatsSummaryMetrics = {
  entriesCount: number
  entrantsCount: number
  averageEntriesPerEntrant?: number | null
  clubsCount: number
  blackJarEntriesCount: number
  winners: ShowStatsOutput['winners'] | null
}

type ShowStatsSummaryQuery = UseQueryResult<ShowStatsOutput, Error> & {
  summary: ShowStatsSummaryMetrics | null
}

type NumericMetricKey = 'entriesCount' | 'entrantsCount' | 'clubsCount' | 'blackJarEntriesCount'

/**
 * Provides normalized show-level metrics for reuse across run surfaces.
 * - Entries count is the canonical value from the show stats API.
 * - Entrants count reflects unique exhibitors for the show.
 * - Clubs count reports unique club affiliations represented.
 * - Black Jar entries count aligns with the dedicated Black Jar program totals.
 */
export function useShowStatsSummary(showId: string | undefined): ShowStatsSummaryQuery {
  const query = useShowStats(showId)

  const summary = useMemo<ShowStatsSummaryMetrics | null>(() => {
    if (!query.data) {
      return null
    }

    return {
      entriesCount: query.data.entriesCount ?? query.data.entrantsCount ?? 0,
      entrantsCount: query.data.entrantsCount ?? 0,
      averageEntriesPerEntrant: query.data.averageEntriesPerEntrant ?? null,
      clubsCount: query.data.clubsCount ?? 0,
      blackJarEntriesCount: query.data.blackJarEntriesCount ?? 0,
      winners: query.data.winners ?? null,
    }
  }, [query.data])

  return {
    ...query,
    summary,
  }
}

type ShowStatsSummaryProps = {
  summary: ShowStatsSummaryMetrics | null
  className?: string
  variant?: 'dashboard' | 'compact'
  enableClubs?: boolean
  additionalMetrics?: Array<{ key: string; label: string; value: number }>
}

type VariantConfig = {
  container: string
  item: string
  label: string
  value: string
  formatValue(value: number): string
}

const VARIANT_CONFIG: Record<NonNullable<ShowStatsSummaryProps['variant']>, VariantConfig> = {
  dashboard: {
    container: 'grid gap-3 sm:grid-cols-2 lg:grid-cols-4',
    item: 'rounded-md border bg-muted/40 px-4 py-3',
    label: 'text-xs font-medium uppercase tracking-wide text-muted-foreground',
    value: 'mt-2 text-2xl font-semibold',
    formatValue: value => value.toLocaleString(),
  },
  compact: {
    container: 'grid gap-4 sm:grid-cols-2 lg:grid-cols-4',
    item: 'rounded-md border px-3 py-2',
    label: 'text-xs font-medium uppercase tracking-wide text-muted-foreground',
    value: 'text-2xl font-semibold',
    formatValue: value => String(value),
  },
}

const BASE_METRICS: Array<{ key: NumericMetricKey; label: string }> = [
  { key: 'entriesCount', label: 'Entries' },
  { key: 'entrantsCount', label: 'Entrants' },
  { key: 'clubsCount', label: 'Clubs' },
  { key: 'blackJarEntriesCount', label: 'Black Jar' },
]

export function ShowStatsSummary({
  summary,
  className,
  variant = 'dashboard',
  enableClubs = true,
  additionalMetrics = [],
}: ShowStatsSummaryProps) {
  if (!summary) {
    return null
  }

  const config = VARIANT_CONFIG[variant]
  const items = [
    ...BASE_METRICS.filter(metric => enableClubs || metric.key !== 'clubsCount').map(metric => ({
      key: metric.key,
      label: metric.label,
      value: summary[metric.key],
    })),
    ...additionalMetrics,
  ]

  return (
    <div className={cn(config.container, className)}>
      {items.map(metric => (
        <div key={metric.key} className={config.item}>
          <p className={config.label}>{metric.label}</p>
          <p className={config.value}>{config.formatValue(metric.value)}</p>
        </div>
      ))}
      <div className={config.item}>
        <p className={config.label}>Average entries per entrant</p>
        <p className={config.value}>
          {summary.averageEntriesPerEntrant == null
            ? '—'
            : summary.averageEntriesPerEntrant.toFixed(2)}
        </p>
        {summary.averageEntriesPerEntrant == null && (
          <p className="text-xs text-muted-foreground">No entrants yet.</p>
        )}
      </div>
    </div>
  )
}

export type { ShowStatsSummaryQuery }
